import '../ClientAIStudioPinturaStylesOverrides.sass'

import { AI_TOOLS, MANUAL_TOOLS, customizeToolbarButtons, getAIToolIcon, getIsComingSoonTool, getManualToolIcon, toolToPinturaFeatureMap } from '../AIStudioUtils'
import { BLUE_100, BLUE_600, GRAY_100, GRAY_400, GRAY_500, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import { Box, Stack, Typography } from '@mui/material'
import { FC, useCallback, useState } from 'react'
import {
  createDefaultColorOptions,
  createDefaultImageScrambler,
  getEditorDefaults
} from '@pqina/pintura'

import { ActionPage } from 'components/common/ActionPage/ActionPage.component'
import { AnalyticsEvent } from 'utils/analytics'
import { ClientAIStudioFooter } from './ClientAIStudioFooter.module'
import { ClientAIStudioNavTabs } from './ClientAIStudioNavTabs.component'
import { GenerateButton } from '../../common/GenerateButton'
import { GenerateLoading } from '../../common/GenerateLoading'
import { MUIBadge } from 'components/common/MUIBadge'
import Modal from 'components/common/Modals/Modal/Modal'
import { PinturaEditor } from '@pqina/react-pintura'
import { ZoomPanel } from '../../common/ZoomPanel'
import { getPinturaLocale } from 'utils/helpers'
import i18n from 'translations/i18n'
import { useClientAIStudio } from './ClientAIStudio.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

const PINTURA_EDITOR_DEFAULTS = getEditorDefaults({
  stickers: ['😎', '🎉', '😄', '👍', '👎'],
})

/**
 * AI Studio photo editing controller for individual visual selected from Gallery/Property Gallery
 * This component provides a user interface for editing images using AI and manual tools.
 * It allows users to select AI tools or manual tools for image editing, and provides a Pintura editor for advanced image manipulation.
 * The old VisualPinturaEditingController is deprecated and should not be used after the migration to AI Studio.
 * @example <ClientAIStudioController />
 */
export const ClientAIStudioController: FC = () => {
  const { t } = useTranslation(['ai_studio_editing', 'common'])

  const {
    previewImage,
    sourceVisualFilename,
    assignmentId,
    handleProcessPinturaUploadImage,
    isProcessingImage,
    onActionPageClose,
    editorRef,
    handleUpdate,
    selectedPinturaFeature,
    setSelectedPinturaFeature,
    selectedAIFeature,
    setSelectedAIFeature,
    logAIStudioEvent,
    setPinturaHistory,
    pinturaHistory,
  } = useClientAIStudio()

  const { hasUserActiveSubscription } = useUserData()
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const [newTool, setNewTool] = useState<string | undefined>(undefined)

  const isExportBetweenEditingCategories = (tool: AI_TOOLS | MANUAL_TOOLS) =>
    (selectedPinturaFeature && !!pinturaHistory?.length) &&
    (selectedPinturaFeature && !Object.values(MANUAL_TOOLS).includes(tool as MANUAL_TOOLS) ||
      selectedAIFeature && !Object.values(AI_TOOLS).includes(tool as AI_TOOLS))

  const handleAIFeatureChange = useCallback((newTool: string) => {
    setSelectedPinturaFeature(undefined)
    setSelectedAIFeature(newTool as AI_TOOLS)
    setPinturaHistory([]) // Reset pintura history when switching to AI tools
  }, [setSelectedPinturaFeature, setSelectedAIFeature, setPinturaHistory])

  const handleManualFeatureChange = useCallback((newTool: string) => {
    setSelectedAIFeature(undefined)
    setSelectedPinturaFeature(toolToPinturaFeatureMap[newTool as MANUAL_TOOLS])
    // TODO: reset AI tools history when switching to pintura after confirmation of user
  }, [setSelectedAIFeature, setSelectedPinturaFeature])

  const handleFromManualToolsToAITools = useCallback((tag: string) => {
    handleProcessPinturaUploadImage(tag, true)

    handleAIFeatureChange(newTool as AI_TOOLS)
    setIsConfirmationOpen(false)

  }, [handleProcessPinturaUploadImage, handleAIFeatureChange, newTool])

  const handleFromAIToolsToManualTools = useCallback((tag: string) => {
    handleManualFeatureChange(newTool as MANUAL_TOOLS)
    setIsConfirmationOpen(false)

  }, [handleManualFeatureChange, newTool])

  // TODO: Change to render something nice when designs for AI Studio non subscriber and/or BETA is ready 
  // - currently todo @BB
  if (!hasUserActiveSubscription) return <></>

  return (
    <ActionPage
      id={`ai-studio-photo-editing-assignment-${assignmentId}-img-${sourceVisualFilename}`}
      title={t('title')}
      onClose={onActionPageClose}
      customNavigationTab={<ClientAIStudioNavTabs />}
      footerChildren={
        <ClientAIStudioFooter
          onClose={onActionPageClose}
          onSave={(tag) => selectedPinturaFeature ? handleProcessPinturaUploadImage(tag, false) : undefined} //TODO implement AI tools save
          isProcessing={isProcessingImage}
          marginRight={4}
        />
      }
    >
      <div style={{ height: '100%', display: 'grid', gridTemplateColumns: '1fr 3fr' }}>
        <Box
          sx={{ backgroundColor: WHITE, padding: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
        >
          <Stack gap={3.2}>

            <Stack>

              <Typography variant="text-sm" fontWeight="600" color={GRAY_900}>{t('ai_tools_title')}</Typography>

              <Stack marginTop={1.6} gap={.8} flexDirection="column" flexWrap="wrap">
                {Object.values(AI_TOOLS).map((tool: AI_TOOLS) => {
                  const isSelectedTool = selectedAIFeature === tool
                  const isComingSoonTool = getIsComingSoonTool(tool)

                  return (
                    <Box
                      key={tool}
                      onClick={() => {
                        if (!isComingSoonTool) {
                          if (isExportBetweenEditingCategories(tool)) {
                            setNewTool(tool)
                            setIsConfirmationOpen(true)
                          } else {
                            handleAIFeatureChange(tool)
                          }
                        }
                      }}
                      sx={{
                        backgroundColor: isComingSoonTool ? GRAY_100 : isSelectedTool ? BLUE_100 : WHITE,
                        padding: '1.4rem 1.8rem',
                        borderRadius: 1.6,
                        border: '1px solid',
                        borderColor: isSelectedTool ? BLUE_600 : GRAY_400,
                        color: isComingSoonTool ? GRAY_500 : isSelectedTool ? GRAY_900 : GRAY_700,
                        textAlign: 'left',
                        fontWeight: isSelectedTool ? '600' : '400',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: isSelectedTool ? BLUE_100 : GRAY_100,
                          fontWeight: isComingSoonTool ? '400' : '600',
                          color: isComingSoonTool ? GRAY_500 : GRAY_900,
                          cursor: isComingSoonTool ? 'not-allowed' : 'pointer',
                        },
                      }}
                    >
                      <Stack gap={1.2} flexDirection="row" alignItems="center" marginLeft={.5}>

                        {getAIToolIcon(tool)}

                        <Typography variant="text-xs">{t(`ai_tools.${tool}`)}</Typography>

                        {isComingSoonTool &&
                          <Stack marginLeft="auto">
                            <MUIBadge label={t('common:coming_soon')} size="xs" color="coral" />
                          </Stack>
                        }

                      </Stack>
                    </Box>
                  )
                })}
              </Stack>

            </Stack>

            <Stack>

              <Typography variant="text-sm" fontWeight="600" color={GRAY_900}>{t('manual_tools_title')}</Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '.8rem',
                  marginTop: 1.6,
                }}
              >
                {Object.values(MANUAL_TOOLS).map((tool) => {
                  const isSelectedTool =
                    String(selectedPinturaFeature).toLowerCase() ===
                    String(toolToPinturaFeatureMap[tool]).toLowerCase()

                  return (
                    <Box
                      key={tool}
                      onClick={() => {
                        if (isExportBetweenEditingCategories(tool)) {
                          setNewTool(tool)
                          setIsConfirmationOpen(true)
                        } else {
                          handleManualFeatureChange(tool)
                        }
                      }}
                      sx={{
                        backgroundColor: isSelectedTool ? BLUE_100 : WHITE,
                        height: '6.4rem',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 1,
                        borderRadius: 1.6,
                        border: '1px solid',
                        borderColor: isSelectedTool ? BLUE_600 : GRAY_400,
                        color: isSelectedTool ? GRAY_900 : GRAY_700,
                        textAlign: 'center',
                        fontWeight: isSelectedTool ? '600' : '400',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: isSelectedTool ? BLUE_100 : GRAY_100,
                          fontWeight: '600',
                          color: GRAY_900,
                        },
                      }}
                    >
                      {getManualToolIcon(tool)}
                      <Typography variant="text-xs" marginTop={0.5}>
                        {t(`manual_tools.${tool}`)}
                      </Typography>
                    </Box>
                  )
                })}
              </Box>

            </Stack>
          </Stack>

        </Box>

        {!selectedPinturaFeature
          ? <Box
            sx={{
              backgroundColor: 'black',
              height: '100%',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden', // Ensures the whole image is visible when portrait or landscape
            }}
          >

            <ZoomPanel>
              <img
                src={previewImage}
                alt="loaded_visual_for_edit"
                style={{
                  maxWidth: '80%',
                  maxHeight: '80%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain', // Ensures the whole image is visible when portrait or landscape
                  display: 'block',
                  margin: 'auto',
                }} />
            </ZoomPanel>

            {isProcessingImage &&
              <GenerateLoading />
            }

            {/* Generate button */}
            <GenerateButton />

          </Box>
          : <div style={{ height: '100%' }}>
            <PinturaEditor
              ref={editorRef}
              {...PINTURA_EDITOR_DEFAULTS}
              src={previewImage}
              onWritehistory={() => setPinturaHistory(editorRef.current?.editor.history.get())}
              onUndo={() => {
                setPinturaHistory(editorRef.current?.editor.history.get())
                logAIStudioEvent(AnalyticsEvent.PINTURA_UNDO)
              }}
              onRedo={() => {
                setPinturaHistory(editorRef.current?.editor.history.get())
                logAIStudioEvent(AnalyticsEvent.PINTURA_REDO)
              }}
              willRenderToolbar={customizeToolbarButtons}
              util={selectedPinturaFeature}
              enableUtils={false} // Disable utils to prevent showing the default toolbar, we render our own toolbar
              // @ts-ignore
              imageScrambler={createDefaultImageScrambler({
                scrambleAmount: 8,
                blurAmount: 20,
                dataSizeScalar: 8
              })}
              onUpdate={handleUpdate}
              cropSelectPresetFilter={'landscape'}
              cropSelectPresetOptions={[
                [undefined, 'Custom'],
                [1, 'Square'],
                // shown when cropSelectPresetFilter is set to 'landscape'
                [2 / 1, '2:1'],
                [3 / 2, '3:2'],
                [4 / 3, '4:3'],
                [5 / 4, '5:4'],
                [16 / 10, '16:10'],
                [16 / 9, '16:9'],
                // shown when cropSelectPresetFilter is set to 'portrait'
                [1 / 2, '1:2'],
                [2 / 3, '2:3'],
                [3 / 4, '3:4'],
                [4 / 5, '4:5'],
                [10 / 16, '10:16'],
                [9 / 16, '9:16'],
              ]}
              fillOptions={[
                // Using the default markup editor colors
                ...Object.values(createDefaultColorOptions()),
              ]}
              locale={getPinturaLocale(i18n.language)}
            />
          </div>
        }
      </div>

      <Modal
        isOpen={isConfirmationOpen}
        title={t('export_progress_title')}
        onClickOutside={() => setIsConfirmationOpen(false)}
        onClose={() => setIsConfirmationOpen(false)}
        footerContent={
          <ClientAIStudioFooter
            onClose={() => setIsConfirmationOpen(false)}
            onSave={(tag) => !!selectedAIFeature
              ? handleFromAIToolsToManualTools(tag)
              : handleFromManualToolsToAITools(tag)
            }
            isProcessing={isProcessingImage}
          />
        }
      >
        <Stack margin={1.6}>
          <Typography variant='text-md'>{t(!!selectedAIFeature ? 'export_progress_from_ai_tools' : 'export_progress_from_manual_tools')}</Typography>
        </Stack>
      </Modal>
    </ActionPage >
  )
}
